import ProfileSetting from "./profile-settings";
import PasswordSettings from "./password-settings";
import { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import SettingsInegrations from "./settings-integrations";
import { AccessBlocked, CtySpinner, ErrorBoundary } from "@circuitry-ai/doc-ui";
import UserProfile from "./userprofile";
import SSOConnectionsList from "./ssoConnectionsList";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import ApiKeysList from "./apikeyslist";
import FgaSettings from "./fga-settings";
import CredentialsSettings from "./credentials-settings";
import ConnectorsSettings from "./connectors-settings";
import ToolPermissionsSettings from "./tool-permissions-settings";
import { useGetFgaUserResourcesQuery, type RootState } from "@circuitry-ai/doc-data";
import SchedulerSettings from "./scheduler-settings";
import WatchlistSettings from "./watchlist-settings";
import InspectionTemplatesSettings from "./inspection-templates-settings";
import WorkQueuesSettings from "./work-queues-settings";
import AuditLogs from "./audit-logs";
import { SETTINGS_NAV_BASE } from "./settings-nav-config";
import PhoneNumbersTab from "./phone-numbers/PhoneNumbersTab";
import { DictionaryTab } from "@circuitry-ai/doc-voice-agent";
import { SchemaMapperPage, SchemaMapperView } from '@circuitry-ai/doc-mapper';

// ErrorBoundary-wrapped components hoisted to module scope
const ProfileSettingWithErrorBoundary = ErrorBoundary(ProfileSetting);
const UserProfileWithErrorBoundary = ErrorBoundary(UserProfile);
const PasswordSettingsWithErrorBoundary = ErrorBoundary(PasswordSettings);
const SettingsInegrationsWithErrorBoundary = ErrorBoundary(SettingsInegrations);
const SSOConnectionsListWithErrorBoundary = ErrorBoundary(SSOConnectionsList);
const ApiKeysListWithErrorBoundary = ErrorBoundary(ApiKeysList);
const FgaSettingsWithErrorBoundary = ErrorBoundary(FgaSettings);
const CredentialsSettingsWithErrorBoundary = ErrorBoundary(CredentialsSettings);
const ConnectorsSettingsWithErrorBoundary = ErrorBoundary(ConnectorsSettings);
const ToolPermissionsSettingsWithErrorBoundary = ErrorBoundary(ToolPermissionsSettings);
const SchedulerSettingsWithErrorBoundary = ErrorBoundary(SchedulerSettings);
const WatchlistSettingsWithErrorBoundary = ErrorBoundary(WatchlistSettings);
const InspectionTemplatesSettingsWithErrorBoundary = ErrorBoundary(InspectionTemplatesSettings);
const WorkQueuesSettingsWithErrorBoundary = ErrorBoundary(WorkQueuesSettings);
const AuditLogsWithErrorBoundary = ErrorBoundary(AuditLogs);
const SchemaMapperPageWithErrorBoundary = ErrorBoundary(SchemaMapperPage);
const SchemaMapperViewWithErrorBoundary = ErrorBoundary(SchemaMapperView);

export interface SettingsProps {
  /** Rendered at /settings/uxgenie when provided (from app to avoid cross-project import) */
  readonly UXGeniePageComponent?: React.ComponentType<{ basePath?: string }>;
  /** Rendered at /settings/uxgenie/create and /settings/uxgenie/:id when provided */
  readonly UXGenieViewComponent?: React.ComponentType<{ basePath?: string }>;
  /** Rendered at /settings/uxgenie/:id/execute and .../edit/:usage_id when provided */
  readonly UXGenieComponent?: React.ComponentType<{ basePath?: string }>;
  /** Rendered at /settings/uxgenie/view/:id when provided */
  readonly UXGenieTemplateViewerComponent?: React.ComponentType<{ basePath?: string }>;
  /** Rendered at /settings/formgenie when provided */
  readonly FormGeniePageComponent?: React.ComponentType<{ basePath?: string }>;
  /** Rendered at /settings/formgenie/create and /settings/formgenie/:id when provided */
  readonly FormGenieViewComponent?: React.ComponentType<{ basePath?: string }>;
  /** Rendered at /settings/formgenie/:id/execute and .../edit/:usage_id when provided */
  readonly FormGenieComponent?: React.ComponentType<{ basePath?: string }>;
  /** Rendered at /settings/genie when provided */
  readonly SmallGenieComponent?: React.ComponentType;
}

/** Same matching as Layout FGA: application_resources entry with relation view by id or name */
function hasApplicationResourceView(
  resources: Array<{ id?: string; name?: string; relation?: string }>,
  resourceId: string
): boolean {
  return resources.some((resource: { id?: string; name?: string; relation?: string }) => {
    const idMatch = resource.id?.toLowerCase() === resourceId.toLowerCase();
    const nameMatch = resource.name?.toLowerCase() === resourceId.toLowerCase();
    const relationMatch = resource.relation === 'view';
    return (idMatch || nameMatch) && relationMatch;
  });
}

// Nav item keys that require application_resource with relation 'view' (others are always allowed)
const SETTINGS_TAB_TO_RESOURCE: Record<string, string> = {
  behavior: 'integrations',
  capabilities: 'integrations',
  connectors: 'integrations',
  'mcp-tools': 'integrations',
  sso: 'sso',
  apikey: 'api_keys',
  fga: 'access_management',
  credentials: 'credentials',
  scheduler: 'scheduler',
  'inspection-templates': 'uxgenie',
  uxgenie: 'uxgenie',
  formgenie: 'uxgenie', // reuse uxgenie resource until formgenie has its own FGA entry
  genie: 'uxgenie',
};

const SETTINGS_BASE = SETTINGS_NAV_BASE;
const SETTINGS_UXGENIE_BASE = '/settings/uxgenie';
const SETTINGS_FORMGENIE_BASE = '/settings/formgenie';
const SETTINGS_GENIE_BASE = '/settings/genie';

export function Settings(props: Readonly<SettingsProps>) {
  const { UXGeniePageComponent, UXGenieViewComponent, UXGenieComponent, UXGenieTemplateViewerComponent, FormGeniePageComponent, FormGenieViewComponent, FormGenieComponent, SmallGenieComponent } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const params = useParams<{ section?: string; mode?: string; aiworker_id?: string; usage_id?: string; aiworker_usage_id?: string; mapper_id?: string }>();

  const { details: user } = useSelector((state: RootState) => state.auth.user);
  const userId = user?.sub;
  const { data, isLoading, isFetching } = useGetFgaUserResourcesQuery(userId || '', {
    skip: !userId,
  });

  // Derive current section from path (e.g. /settings/details -> 'details')
  const currentSection = pathname.startsWith(SETTINGS_BASE)
    ? pathname.slice(SETTINGS_BASE.length).replace(/^\//, '').split('/')[0] || 'details'
    : 'details';

  // Redirect /settings or /settings/ to /settings/profile
  useEffect(() => {
    if (pathname === '/settings' || pathname === '/settings/') {
      navigate('/settings/profile', { replace: true });
    }
  }, [pathname, navigate]);

  const UXGenieWrapped = useMemo(
    () =>
      UXGeniePageComponent
        ? ErrorBoundary(UXGeniePageComponent as unknown as React.ComponentType<Record<string, unknown>>)
        : null,
    [UXGeniePageComponent]
  );

  const UXGenieViewWrapped = useMemo(
    () =>
      UXGenieViewComponent
        ? ErrorBoundary(UXGenieViewComponent as unknown as React.ComponentType<Record<string, unknown>>)
        : null,
    [UXGenieViewComponent]
  );

  const UXGenieExecuteWrapped = useMemo(
    () =>
      UXGenieComponent
        ? ErrorBoundary(UXGenieComponent as unknown as React.ComponentType<Record<string, unknown>>)
        : null,
    [UXGenieComponent]
  );

  const UXGenieTemplateViewerWrapped = useMemo(
    () =>
      UXGenieTemplateViewerComponent
        ? ErrorBoundary(UXGenieTemplateViewerComponent as unknown as React.ComponentType<Record<string, unknown>>)
        : null,
    [UXGenieTemplateViewerComponent]
  );

  const FormGenieWrapped = useMemo(
    () =>
      FormGeniePageComponent
        ? ErrorBoundary(FormGeniePageComponent as unknown as React.ComponentType<Record<string, unknown>>)
        : null,
    [FormGeniePageComponent]
  );

  const FormGenieViewWrapped = useMemo(
    () =>
      FormGenieViewComponent
        ? ErrorBoundary(FormGenieViewComponent as unknown as React.ComponentType<Record<string, unknown>>)
        : null,
    [FormGenieViewComponent]
  );

  const FormGenieExecuteWrapped = useMemo(
    () =>
      FormGenieComponent
        ? ErrorBoundary(FormGenieComponent as unknown as React.ComponentType<Record<string, unknown>>)
        : null,
    [FormGenieComponent]
  );

  const SmallGenieWrapped = useMemo(
    () =>
      SmallGenieComponent
        ? ErrorBoundary(SmallGenieComponent as unknown as React.ComponentType<Record<string, unknown>>)
        : null,
    [SmallGenieComponent]
  );

  const renderSettingsSection = () => {
    const section = currentSection.toLowerCase();
    switch (section) {
      case 'details':
        return <ProfileSettingWithErrorBoundary />;
      case 'profile':
        return <UserProfileWithErrorBoundary />;
      case 'email':
        return <PasswordSettingsWithErrorBoundary />;
      case 'behavior':
        return <SettingsInegrationsWithErrorBoundary currentSection="behavior" />;
      case 'capabilities':
        return <SettingsInegrationsWithErrorBoundary currentSection="capabilities" />;
      case 'sso':
        return <SSOConnectionsListWithErrorBoundary />;
      case 'apikey':
        return <ApiKeysListWithErrorBoundary />;
      case 'fga':
        return <FgaSettingsWithErrorBoundary />;
      case 'credentials':
        return <CredentialsSettingsWithErrorBoundary />;
      case 'connectors':
        return <ConnectorsSettingsWithErrorBoundary />;
      case 'mcp-tools':
        return <ToolPermissionsSettingsWithErrorBoundary />;
      case 'uxgenie':
        if (params.aiworker_usage_id && UXGenieTemplateViewerWrapped) {
          return <UXGenieTemplateViewerWrapped basePath={SETTINGS_UXGENIE_BASE} />;
        }
        if (params.usage_id && UXGenieExecuteWrapped) {
          return <UXGenieExecuteWrapped basePath={SETTINGS_UXGENIE_BASE} />;
        }
        if (params.aiworker_id && pathname.includes('/execute') && UXGenieExecuteWrapped) {
          return <UXGenieExecuteWrapped basePath={SETTINGS_UXGENIE_BASE} />;
        }
        if (params.mode !== undefined && UXGenieViewWrapped) {
          return <UXGenieViewWrapped basePath={SETTINGS_UXGENIE_BASE} />;
        }
        return UXGenieWrapped ? <UXGenieWrapped basePath={SETTINGS_UXGENIE_BASE} /> : null;
      case 'formgenie':
        if (params.usage_id && FormGenieExecuteWrapped) {
          return <FormGenieExecuteWrapped basePath={SETTINGS_FORMGENIE_BASE} />;
        }
        if (params.aiworker_id && pathname.includes('/execute') && FormGenieExecuteWrapped) {
          return <FormGenieExecuteWrapped basePath={SETTINGS_FORMGENIE_BASE} />;
        }
        if (params.mode !== undefined && FormGenieViewWrapped) {
          return <FormGenieViewWrapped basePath={SETTINGS_FORMGENIE_BASE} />;
        }
        return FormGenieWrapped ? <FormGenieWrapped basePath={SETTINGS_FORMGENIE_BASE} /> : null;
      case 'genie':
        return SmallGenieWrapped ? <SmallGenieWrapped /> : null;
      case 'schema-mapper':
        if (params.mode !== undefined) {
          return <SchemaMapperViewWithErrorBoundary />;
        }
        return <SchemaMapperPageWithErrorBoundary />;
      case 'scheduler':
        return <SchedulerSettingsWithErrorBoundary />;
      case 'work-queues':
        return <WorkQueuesSettingsWithErrorBoundary />;
      case 'watchlist':
        return <WatchlistSettingsWithErrorBoundary />;
      case 'inspection-templates':
        return <InspectionTemplatesSettingsWithErrorBoundary />;
      case 'phone-numbers':
        return <PhoneNumbersTab />;
      case 'dictionary':
        return <DictionaryTab />;
      case 'audit-logs':
        return <AuditLogsWithErrorBoundary />;
      default:
        return <ProfileSettingWithErrorBoundary />;
    }
  };

  const renderMainPanel = () => {
    const section = currentSection.toLowerCase();
    const requiredResource = SETTINGS_TAB_TO_RESOURCE[section];
    if (!requiredResource || !userId) {
      return renderSettingsSection();
    }
    const appResources = data?.data?.resources?.application_resources;
    // Only block while there is no decision to make yet. A background refetch keeps the
    // cached decision on screen — swapping in the spinner would unmount the section and
    // remount it once the refetch settles, restarting whatever it was loading.
    if (isLoading || (isFetching && !Array.isArray(appResources))) {
      return (
        <div className="flex-1 flex items-center justify-center min-h-[200px]">
          <CtySpinner size="lg" />
        </div>
      );
    }
    if (!Array.isArray(appResources)) {
      return renderSettingsSection();
    }
    if (!hasApplicationResourceView(appResources, requiredResource)) {
      return <AccessBlocked resourceName={requiredResource} />;
    }
    return renderSettingsSection();
  };

  return (
    <div className="settings-page flex flex-col h-full">
      <div className="flex flex-1 min-h-0">
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 w-full pt-4 pl-6 pr-6 pb-3">{renderMainPanel()}</div>
        </main>
      </div>
    </div>
  );
}

export default Settings;
